import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, User, Lock, Save, Eye, EyeOff, 
  Shield, Mail, Briefcase, Check, AlertCircle, Factory, ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSettingsPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Username change state
  const [newUsername, setNewUsername] = useState("");
  const [usernamePassword, setUsernamePassword] = useState("");
  const [showUsernamePassword, setShowUsernamePassword] = useState(false);
  const [isChangingUsername, setIsChangingUsername] = useState(false);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Factory portal credentials
  const [portals, setPortals] = useState([]);
  const [portalsLoading, setPortalsLoading] = useState(false);
  const [showPortalPassword, setShowPortalPassword] = useState({});

  useEffect(() => {
    const adminAuth = sessionStorage.getItem("adminAuth");
    const storedUser = sessionStorage.getItem("adminUser");
    
    if (adminAuth !== "true" || !storedUser) {
      navigate("/admin");
    } else {
      setIsAuthenticated(true);
      try {
        const user = JSON.parse(storedUser);
        setCurrentUser(user);
        setNewUsername(user.username || "");
      } catch (e) {
        navigate("/admin");
      }
      fetchPortals();
    }
  }, [navigate]);

  const fetchPortals = async () => {
    try {
      setPortalsLoading(true);
      const res = await axios.get(`${API}/factory-portals`);
      setPortals(res.data?.portals || []);
    } catch (e) {
      // Non-fatal: factory portals are admin-only secondary data; warn so it shows
      // up in browser console for debugging without breaking the rest of the page.
      // eslint-disable-next-line no-console
      console.warn("Factory portals failed to load:", e?.message || e);
    } finally {
      setPortalsLoading(false);
    }
  };

  const updatePortalField = (portalKey, field, value) => {
    setPortals((prev) => prev.map((p) => (p.portal_key === portalKey ? { ...p, [field]: value } : p)));
  };

  const savePortal = async (portal) => {
    try {
      await axios.put(`${API}/factory-portals/${portal.portal_key}`, portal);
      toast.success(`${portal.name} credentials saved`);
    } catch (e) {
      toast.error("Failed to save portal credentials");
    }
  };

  const handleChangeUsername = async (e) => {
    e.preventDefault();
    
    if (!newUsername || newUsername.length < 3) {
      toast.error("Username must be at least 3 characters");
      return;
    }
    
    if (!usernamePassword) {
      toast.error("Please enter your password to confirm");
      return;
    }
    
    if (newUsername === currentUser?.username) {
      toast.info("Username is the same as current");
      return;
    }
    
    setIsChangingUsername(true);
    
    try {
      const response = await axios.post(`${API}/admin/auth/change-username`, {
        user_id: currentUser.id,
        new_username: newUsername,
        password: usernamePassword
      });
      
      if (response.data.success) {
        // Update session storage
        const updatedUser = { ...currentUser, username: newUsername };
        sessionStorage.setItem("adminUser", JSON.stringify(updatedUser));
        setCurrentUser(updatedUser);
        setUsernamePassword("");
        toast.success("Username changed successfully!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change username");
    } finally {
      setIsChangingUsername(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!currentPassword) {
      toast.error("Please enter your current password");
      return;
    }
    
    if (!newPassword || newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    
    if (currentPassword === newPassword) {
      toast.error("New password must be different from current password");
      return;
    }
    
    setIsChangingPassword(true);
    
    try {
      const response = await axios.post(`${API}/admin/auth/change-password`, {
        user_id: currentUser.id,
        old_password: currentPassword,
        new_password: newPassword
      });
      
      if (response.data.success) {
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        toast.success("Password changed successfully!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setIsChangingPassword(false);
    }
  };

  if (!isAuthenticated || !currentUser) return null;

  return (
    <div className="min-h-screen pt-20 pb-12">
      {/* Header */}
      <section className="py-8 border-b border-white/5">
        <div className="max-w-[800px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <button
              onClick={() => navigate("/admin")}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft size={16} />
              Back to Dashboard
            </button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-500 to-slate-700 flex items-center justify-center">
                <Shield size={24} className="text-white" />
              </div>
              <div>
                <h1 className="font-serif text-2xl sm:text-3xl tracking-tight">
                  Account Settings
                </h1>
                <p className="text-sm text-muted-foreground">
                  Manage your username and password
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8">
        <div className="max-w-[800px] mx-auto px-6 md:px-12 space-y-8">
          
          {/* Current Profile Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-white/10 rounded-lg p-6"
          >
            <h3 className="font-serif text-lg mb-4 flex items-center gap-2">
              <User size={18} className="text-primary" />
              Profile Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-background/50 rounded-lg p-4 border border-white/5">
                <p className="text-xs text-muted-foreground mb-1">Username</p>
                <p className="font-medium">{currentUser.username}</p>
              </div>
              <div className="bg-background/50 rounded-lg p-4 border border-white/5">
                <p className="text-xs text-muted-foreground mb-1">Full Name</p>
                <p className="font-medium">{currentUser.full_name || "Not set"}</p>
              </div>
              <div className="bg-background/50 rounded-lg p-4 border border-white/5">
                <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                  <Mail size={12} /> Email
                </p>
                <p className="font-medium">{currentUser.email || "Not set"}</p>
              </div>
              <div className="bg-background/50 rounded-lg p-4 border border-white/5">
                <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                  <Briefcase size={12} /> Role
                </p>
                <p className="font-medium capitalize">{currentUser.role || "Employee"}</p>
              </div>
            </div>
          </motion.div>

          {/* Change Username */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card border border-white/10 rounded-lg p-6"
          >
            <h3 className="font-serif text-lg mb-4 flex items-center gap-2">
              <User size={18} className="text-blue-400" />
              Change Username
            </h3>
            <form onSubmit={handleChangeUsername} className="space-y-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-2">
                  New Username
                </label>
                <Input
                  type="text"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  placeholder="Enter new username"
                  className="bg-background border-white/10"
                  minLength={3}
                  data-testid="new-username-input"
                />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-2">
                  Confirm with Password
                </label>
                <div className="relative">
                  <Input
                    type={showUsernamePassword ? "text" : "password"}
                    value={usernamePassword}
                    onChange={(e) => setUsernamePassword(e.target.value)}
                    placeholder="Enter your password"
                    className="bg-background border-white/10 pr-10"
                    data-testid="username-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowUsernamePassword(!showUsernamePassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showUsernamePassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={isChangingUsername || !newUsername || !usernamePassword}
                className="bg-blue-500 hover:bg-blue-600 text-white"
                data-testid="change-username-btn"
              >
                {isChangingUsername ? (
                  "Changing..."
                ) : (
                  <>
                    <Save size={16} className="mr-2" />
                    Change Username
                  </>
                )}
              </Button>
            </form>
          </motion.div>

          {/* Change Password */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card border border-white/10 rounded-lg p-6"
          >
            <h3 className="font-serif text-lg mb-4 flex items-center gap-2">
              <Lock size={18} className="text-amber-400" />
              Change Password
            </h3>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-2">
                  Current Password
                </label>
                <div className="relative">
                  <Input
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    className="bg-background border-white/10 pr-10"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-2">
                  New Password
                </label>
                <div className="relative">
                  <Input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 characters)"
                    className="bg-background border-white/10 pr-10"
                    minLength={6}
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className={`bg-background border-white/10 pr-10 ${
                      confirmPassword && newPassword !== confirmPassword 
                        ? "border-red-500/50" 
                        : confirmPassword && newPassword === confirmPassword 
                        ? "border-green-500/50" 
                        : ""
                    }`}
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                    <AlertCircle size={12} /> Passwords do not match
                  </p>
                )}
                {confirmPassword && newPassword === confirmPassword && (
                  <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
                    <Check size={12} /> Passwords match
                  </p>
                )}
              </div>
              <Button
                type="submit"
                disabled={isChangingPassword || !currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="change-password-btn"
              >
                {isChangingPassword ? (
                  "Changing..."
                ) : (
                  <>
                    <Lock size={16} className="mr-2" />
                    Change Password
                  </>
                )}
              </Button>
            </form>
          </motion.div>

          {/* Factory Portals (RCMTM / MX MTM) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-card border border-white/10 rounded-lg p-6"
          >
            <h3 className="font-medium text-base mb-1 flex items-center gap-2">
              <Factory size={16} className="text-amber-400" /> Factory Portals
            </h3>
            <p className="text-xs text-muted-foreground mb-4">
              Shared shop credentials used by the embedded RCMTM and MX MTM pages.
            </p>
            <div className="space-y-5">
              {portalsLoading && <p className="text-xs text-muted-foreground">Loading…</p>}
              {portals.map((p) => (
                <div key={p.portal_key} className="border border-white/10 rounded-md p-4 bg-muted/10">
                  <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                    <div>
                      <p className="text-sm font-medium">{p.name}</p>
                      <a href={p.url} target="_blank" rel="noreferrer" className="text-[11px] text-primary inline-flex items-center gap-1">
                        {p.url} <ExternalLink size={10} />
                      </a>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => savePortal(p)}
                      data-testid={`save-portal-${p.portal_key}`}
                    >
                      <Save size={14} className="mr-1" /> Save
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground">Portal URL</label>
                      <Input
                        value={p.url || ""}
                        onChange={(e) => updatePortalField(p.portal_key, "url", e.target.value)}
                        data-testid={`portal-url-${p.portal_key}`}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Username</label>
                      <Input
                        value={p.username || ""}
                        onChange={(e) => updatePortalField(p.portal_key, "username", e.target.value)}
                        data-testid={`portal-username-${p.portal_key}`}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs text-muted-foreground">Password</label>
                      <div className="flex gap-2">
                        <Input
                          type={showPortalPassword[p.portal_key] ? "text" : "password"}
                          value={p.password || ""}
                          onChange={(e) => updatePortalField(p.portal_key, "password", e.target.value)}
                          data-testid={`portal-password-${p.portal_key}`}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowPortalPassword((prev) => ({ ...prev, [p.portal_key]: !prev[p.portal_key] }))}
                          data-testid={`portal-toggle-password-${p.portal_key}`}
                        >
                          {showPortalPassword[p.portal_key] ? <EyeOff size={14} /> : <Eye size={14} />}
                        </Button>
                      </div>
                    </div>
                    {p.notes && (
                      <div className="md:col-span-2">
                        <p className="text-[11px] text-muted-foreground italic">{p.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Security Tips */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-slate-500/10 border border-slate-500/20 rounded-lg p-6"
          >
            <h3 className="font-medium text-sm mb-3 flex items-center gap-2">
              <Shield size={16} className="text-slate-400" />
              Security Tips
            </h3>
            <ul className="text-xs text-muted-foreground space-y-2">
              <li className="flex items-start gap-2">
                <Check size={12} className="mt-0.5 text-green-400 shrink-0" />
                Use a strong password with at least 6 characters
              </li>
              <li className="flex items-start gap-2">
                <Check size={12} className="mt-0.5 text-green-400 shrink-0" />
                Include a mix of letters, numbers, and special characters
              </li>
              <li className="flex items-start gap-2">
                <Check size={12} className="mt-0.5 text-green-400 shrink-0" />
                Do not use the same password across multiple accounts
              </li>
              <li className="flex items-start gap-2">
                <Check size={12} className="mt-0.5 text-green-400 shrink-0" />
                Change your password regularly for better security
              </li>
            </ul>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AdminSettingsPage;
